import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { Link, router, Stack } from 'expo-router';
import {
  BanIcon,
  BellIcon,
  CalendarClockIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  CreditCardIcon,
  EyeIcon,
  ImagesIcon,
  LinkIcon,
  ListChecksIcon,
  MapPinIcon,
  PlayIcon,
  RouteIcon,
  ScanLineIcon,
  TicketIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  ActivityIndicator,
  Alert,
  BackHandler,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AdminEventNotificationPanel } from '@/components/events/admin-event-notification-panel';
import { getCurrentTime } from '@/lib/dev-clock';
import { AdminEventPaymentsPanel } from '@/components/events/admin-event-payments-panel';
import { DateTimeField } from '@/components/events/date-time-field';
import { EventAttendees } from '@/components/events/event-attendees';
import { EventEditorRow } from '@/components/events/event-editor-row';
import { firstContentSafetyFailure } from '@sixam/shared/content-safety-core';
import { EventEditorSheet, EventEditorSurface } from '@/components/events/event-editor-sheet';
import { EventPlaceSearch } from '@/components/events/event-place-search';
import { EventRegistrationFormBuilder } from '@/components/events/event-registration-form';
import { RouteMapPreview } from '@/components/routes/route-map-preview';
import { RoutePlanner } from '@/components/routes/route-planner';
import { RouteSelector } from '@/components/routes/route-selector';
import { useSession } from '@/components/session-provider';
import { SIXAM_MONDAY_ROUTE } from '@/lib/default-route';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { formatRouteDistance } from '@sixam/shared/route-core';
import {
  EMPTY_EVENT_EDIT_ACTIVITY,
  eventHasEditActivity,
  eventUpdatePromptCopy,
  eventUpdateReasons,
  validateEventPostRegistrationEdit,
  type EventEditActivitySummary,
  type EventUpdateReason,
} from '@sixam/shared/event-editing-core';
import {
  eventLinkLabelSuggestion,
  eventLinksValidationError,
  eventMediaCountError,
  eventMediaExtension,
  eventMediaUploadError,
  formatEventMediaUploadLimit,
  MAX_EVENT_LINKS,
  MAX_EVENT_MEDIA_ITEMS,
  normalizeEventLinkUrl,
  normalizeEventLinksDraft,
  type EventLinkDraft,
  type NormalizedEventLink,
} from '@sixam/shared/event-assets-core';
import {
  deleteEventMedia,
  eventMediaPublicUrl,
  fetchEventAssets,
  saveEventLinks,
  storagePublicUrl,
  uploadLocalFileToStorage,
  uploadEventMediaAsset,
  type EventMedia,
} from '@/lib/event-assets';
import { isCheckInWindowOpen, type EventCheckInMode } from '@sixam/shared/event-checkin-core';
import {
  isValidEventPriceThb,
  MAX_EVENT_PRICE_THB,
  type EventPaymentMethod,
} from '@/lib/event-registration';
import {
  EMPTY_REGISTRATION_FORM_SCHEMA,
  normalizeRegistrationFormSchema,
  registrationFormHasQuestions,
  registrationFormSchemaToJson,
  validateRegistrationFormSchema,
  type RegistrationFormSchema,
} from '@sixam/shared/event-registration-form-core';
import {
  createEventScheduleDefaults,
  editorSheetForValidationField,
  ensureValidEnd,
  eventScheduleError,
  formatEditorSchedule,
  MAX_EVENT_DESCRIPTION_CHARACTERS,
  MAX_EVENT_TITLE_CHARACTERS,
  numericInputValue,
  paidEventDetailsError,
  replaceCalendarDate,
  shouldPromptForEventUpdate,
  validateEventEditorText,
  type EventEditorSaveIntent,
  type EventEditorSheetId,
} from '@/lib/event-editor-core';
import { eventRouteFlowReducer } from '@/lib/event-flow-presentation';
import {
  invalidateEventData,
  type Event,
  type EventStatus,
  type PlaceSelection,
} from '@/lib/events';
import { supabase } from '@sixam/supabase/client';
import { useAnalytics } from '@/components/analytics-provider';
import { fetchRouteVersion, type RouteLibraryItem } from '@/lib/routes';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export type EventEditorCommunity = Readonly<{
  id: string;
  name: string;
  slug: string;
}>;

type EventFormProps = Readonly<{
  communities?: readonly EventEditorCommunity[];
  embedded?: boolean;
  event?: Event;
  initialEditActivity?: EventEditActivitySummary;
  initialRouteVersionId?: string | null;
  initialRoutes?: readonly RouteLibraryItem[];
  initialSheet?: EventEditorSheetId | null;
  managementLevel?: 'member' | 'operator';
  onClose?: () => void;
  onSaved: (event: Event, meta: EventFormSaveMeta) => void;
  onTenantChange?: (tenantId: string) => void;
  personalEvent?: boolean;
  tenantId: string | null;
  tenantName?: string | null;
  tenantSlug?: string | null;
}>;

function eventEditorTitle(
  isEditing: boolean,
  isOperator: boolean,
  isPersonalEvent: boolean
): string {
  if (isPersonalEvent) return isEditing ? 'Edit event' : 'New event';
  if (isEditing) return isOperator ? 'Edit event' : 'Edit run';
  return isOperator ? 'New event' : 'New run';
}

function eventVisibilityDetail(value: 'draft' | 'published', isOperator: boolean): string {
  if (value === 'published') return 'Visible to members and the public.';
  return isOperator
    ? 'Visible only to community admins.'
    : 'Visible only to you and community admins.';
}

export type EventFormSaveMeta = Readonly<{
  notificationReasons: EventUpdateReason[];
}>;

type EventFormDraft = Readonly<{
  capacity: string;
  description: string;
  endsAt: Date;
  eventLinks: readonly EventLinkDraft[];
  mediaItemCount: number;
  paymentMethods: readonly EventPaymentMethod[];
  pendingMediaAssets: readonly ImagePicker.ImagePickerAsset[];
  place: PlaceSelection | null;
  registrationFormSchema: RegistrationFormSchema;
  registrationPriceThb: string;
  startsAt: Date;
  tenantId: string | null;
  title: string;
  thaiQrStoragePath: string;
  thaiQrPendingAsset: ImagePicker.ImagePickerAsset | null;
  transferDetails: string;
}>;

type EventFormDraftValidation =
  | Readonly<{ error: string; field: string; valid: false }>
  | Readonly<{
      endsAt: Date;
      normalizedEventLinks: NormalizedEventLink[];
      normalizedRegistrationForm: RegistrationFormSchema;
      normalizedDescription: string;
      normalizedTitle: string;
      parsedCapacity: number | null;
      parsedRegistrationPrice: number;
      place: PlaceSelection;
      tenantId: string | null;
      valid: true;
    }>;

function toPlaceSelection(event?: Event): PlaceSelection | null {
  if (!event) return null;
  return {
    address: event.place_address,
    lat: event.place_lat,
    lng: event.place_lng,
    name: event.place_name,
    placeId: event.place_id,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function validateEventFormDraft(
  draft: EventFormDraft,
  paidEventsEnabled: boolean,
  eventDescriptionEnabled: boolean,
  allowPersonalEvent: boolean
): EventFormDraftValidation {
  if (!draft.tenantId && !allowPersonalEvent) {
    return { error: 'Select the community creating this event.', field: 'tenant', valid: false };
  }
  const textValidation = validateEventEditorText(
    draft.title,
    draft.description,
    eventDescriptionEnabled
  );
  if (!textValidation.valid) return textValidation;

  // Told to the author before they submit. The database trigger enforces the
  // same rules on write, so this is the courtesy rather than the guard.
  const unsafeText = firstContentSafetyFailure([
    { field: 'title', value: draft.title },
    { field: 'description', value: draft.description },
  ]);
  if (unsafeText) {
    return { error: unsafeText.message, field: 'title', valid: false };
  }
  const scheduleError = eventScheduleError(draft.startsAt, draft.endsAt);
  if (scheduleError) {
    return { error: scheduleError, field: 'endsAt', valid: false };
  }
  if (!draft.place) {
    return { error: 'Pick a location for the event.', field: 'place', valid: false };
  }

  const parsedCapacity = draft.capacity.trim() ? Number(draft.capacity.trim()) : null;
  if (parsedCapacity != null && (!Number.isInteger(parsedCapacity) || parsedCapacity < 1)) {
    return {
      error: 'Capacity must be a whole number of at least 1.',
      field: 'capacity',
      valid: false,
    };
  }

  const parsedRegistrationPrice = draft.registrationPriceThb.trim()
    ? Number(draft.registrationPriceThb.trim())
    : 0;
  if (!isValidEventPriceThb(parsedRegistrationPrice)) {
    return {
      error: `Registration price must be a whole THB amount up to ${MAX_EVENT_PRICE_THB}.`,
      field: 'registrationPrice',
      valid: false,
    };
  }
  if (!paidEventsEnabled && parsedRegistrationPrice > 0) {
    return {
      error: 'Paid events are disabled for this release. Set the registration price to 0.',
      field: 'registrationPrice',
      valid: false,
    };
  }
  if (parsedRegistrationPrice > 0 && !draft.paymentMethods.includes('thai_qr')) {
    return {
      error: 'Paid events currently require Thai QR payment.',
      field: 'paymentMethods',
      valid: false,
    };
  }
  const paymentDetailsError = paidEventDetailsError(parsedRegistrationPrice, {
    thaiQrStoragePath: draft.thaiQrStoragePath || draft.thaiQrPendingAsset?.uri || '',
    transferDetails: draft.transferDetails,
  });
  if (paymentDetailsError) {
    return {
      error: paymentDetailsError,
      field: draft.thaiQrStoragePath || draft.thaiQrPendingAsset ? 'transferDetails' : 'thaiQr',
      valid: false,
    };
  }

  const mediaError =
    eventMediaCountError(draft.mediaItemCount) ??
    pendingMediaValidationError(draft.pendingMediaAssets);
  if (mediaError) return { error: mediaError, field: 'media', valid: false };

  const linksError = eventLinksValidationError(draft.eventLinks);
  if (linksError) return { error: linksError, field: 'eventLinks', valid: false };

  const formValidation = validateRegistrationFormSchema(draft.registrationFormSchema);
  if (!formValidation.valid) {
    return {
      error: formValidation.error ?? 'Attendee questions are incomplete.',
      field: 'registrationForm',
      valid: false,
    };
  }

  return {
    endsAt: draft.endsAt,
    normalizedDescription: textValidation.description,
    normalizedEventLinks: normalizeEventLinksDraft(draft.eventLinks),
    normalizedRegistrationForm: normalizeRegistrationFormSchema(draft.registrationFormSchema),
    normalizedTitle: textValidation.title,
    parsedCapacity,
    parsedRegistrationPrice,
    place: draft.place,
    tenantId: draft.tenantId,
    valid: true,
  };
}

function eventSaveErrorMessage(message: string): string {
  if (message.includes('personal_event_limit_reached')) {
    return 'You can have up to 3 active personal events. Once one ends, you can create another.';
  }
  if (message.includes('personal_event_creator_required')) {
    return 'Personal events need a signed-in creator.';
  }
  return message;
}

function pendingMediaValidationError(
  pendingMediaAssets: readonly ImagePicker.ImagePickerAsset[]
): string | null {
  for (const asset of pendingMediaAssets) {
    const uploadError = eventMediaUploadError({
      duration: asset.duration == null ? null : asset.duration / 1000,
      mediaKind: asset.type,
      name: asset.fileName,
      size: asset.fileSize,
      type: asset.mimeType,
    });
    if (uploadError) return uploadError;
  }
  return null;
}

type SheetValidationError = Readonly<{ field: string; message: string }>;

type SheetValidationContext = Readonly<{
  capacityError: string | null;
  contentCount: number;
  eventLinks: readonly EventLinkDraft[];
  endsAt: Date;
  hasThaiQr: boolean;
  paymentError: string | null;
  pendingMediaAssets: readonly ImagePicker.ImagePickerAsset[];
  place: PlaceSelection | null;
  priceError: string | null;
  registrationFormSchema: RegistrationFormSchema;
  startsAt: Date;
  validationField: string | null;
}>;

function registrationSheetValidationError(
  context: Pick<
    SheetValidationContext,
    'capacityError' | 'hasThaiQr' | 'paymentError' | 'priceError'
  >
): SheetValidationError | null {
  if (context.capacityError) {
    return { field: 'capacity', message: context.capacityError };
  }
  if (context.priceError) {
    return { field: 'registrationPrice', message: context.priceError };
  }
  if (context.paymentError) {
    return {
      field: context.hasThaiQr ? 'transferDetails' : 'thaiQr',
      message: context.paymentError,
    };
  }
  return null;
}

function scheduleSheetValidationError(
  context: Pick<SheetValidationContext, 'endsAt' | 'startsAt'>
): SheetValidationError | null {
  const message = eventScheduleError(context.startsAt, context.endsAt);
  return message ? { field: 'endsAt', message } : null;
}

function locationSheetValidationError(
  context: Pick<SheetValidationContext, 'place' | 'validationField'>
): SheetValidationError | null {
  if (context.validationField !== 'place' || context.place) return null;
  return { field: 'place', message: 'Pick a location for the event.' };
}

function questionsSheetValidationError(
  schema: RegistrationFormSchema
): SheetValidationError | null {
  const validation = validateRegistrationFormSchema(schema);
  return validation.valid
    ? null
    : {
        field: 'registrationForm',
        message: validation.error ?? 'Attendee questions are incomplete.',
      };
}

function mediaSheetValidationError(
  context: Pick<SheetValidationContext, 'contentCount' | 'pendingMediaAssets'>
): SheetValidationError | null {
  const message =
    eventMediaCountError(context.contentCount) ??
    pendingMediaValidationError(context.pendingMediaAssets);
  return message ? { field: 'media', message } : null;
}

function linksSheetValidationError(
  eventLinks: readonly EventLinkDraft[]
): SheetValidationError | null {
  const message = eventLinksValidationError(eventLinks);
  return message ? { field: 'eventLinks', message } : null;
}

function sheetValidationError(
  sheet: EventEditorSheetId,
  context: SheetValidationContext
): SheetValidationError | null {
  switch (sheet) {
    case 'schedule':
      return scheduleSheetValidationError(context);
    case 'location':
      return locationSheetValidationError(context);
    case 'registration':
      return registrationSheetValidationError(context);
    case 'questions':
      return questionsSheetValidationError(context.registrationFormSchema);
    case 'media':
      return mediaSheetValidationError(context);
    case 'links':
      return linksSheetValidationError(context.eventLinks);
    default:
      return null;
  }
}

async function saveEventFormAssets({
  actorId,
  eventId,
  existingMediaCount,
  links,
  pendingMediaAssets,
  removedMedia,
  tenantId,
  title,
}: Readonly<{
  actorId?: string | null;
  eventId: string;
  existingMediaCount: number;
  links: readonly NormalizedEventLink[];
  pendingMediaAssets: readonly ImagePicker.ImagePickerAsset[];
  removedMedia: readonly EventMedia[];
  tenantId: string;
  title: string;
}>): Promise<void> {
  await saveEventLinks(eventId, tenantId, links, actorId);
  await deleteEventMedia(removedMedia);
  for (const [index, asset] of pendingMediaAssets.entries()) {
    await uploadEventMediaAsset({
      actorId,
      altText: title.trim(),
      asset,
      eventId,
      sequence: existingMediaCount + index,
      tenantId,
    });
  }
}

function mediaLabel(media: Pick<EventMedia, 'media_type' | 'storage_path'>): string {
  return `${media.media_type === 'video' ? 'Video' : 'Photo'} · ${
    media.storage_path.split('/').pop() ?? media.storage_path
  }`;
}

function assetLabel(asset: ImagePicker.ImagePickerAsset, index: number): string {
  return `${asset.type === 'video' ? 'Video' : 'Photo'} · ${
    asset.fileName ?? `selected-${index + 1}`
  }`;
}

function FlatField({
  accessibilityLabel,
  editable = true,
  keyboardType,
  onBlur,
  onChangeText,
  placeholder,
  value,
}: Readonly<{
  accessibilityLabel: string;
  editable?: boolean;
  keyboardType?: 'default' | 'number-pad' | 'url';
  onBlur?: () => void;
  onChangeText: (value: string) => void;
  placeholder: string;
  value: string;
}>) {
  return (
    <TextInput
      accessibilityLabel={accessibilityLabel}
      className="min-h-12 border-b border-border px-1 font-body text-base text-foreground outline-none"
      editable={editable}
      keyboardType={keyboardType}
      onBlur={onBlur}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#71717A"
      value={value}
    />
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function EventForm({
  communities = [],
  embedded = false,
  event,
  initialEditActivity,
  initialRouteVersionId,
  initialRoutes,
  initialSheet = null,
  managementLevel = 'operator',
  onClose,
  onSaved,
  onTenantChange,
  personalEvent = false,
  tenantId,
  tenantName,
  tenantSlug,
}: EventFormProps) {
  const posthog = useAnalytics();
  const isEditing = Boolean(event);
  const eventDescriptionEnabled = useReleaseFeatureFlag('eventDescription') === true;
  const eventDraftPublishEnabled = useReleaseFeatureFlag('eventDraftPublish') === true;
  const eventMediaEnabled = useReleaseFeatureFlag('eventMedia') === true;
  const eventPaymentsEnabled = useReleaseFeatureFlag('eventPayments') === true;
  const paymentManagementEnabled = useReleaseFeatureFlag('paymentManagement') === true;
  const paymentReviewEnabled = useReleaseFeatureFlag('paymentReview') === true;
  const registrationFormsEnabled = useReleaseFeatureFlag('registrationForms') === true;
  const routeLibraryReleaseEnabled = useReleaseFeatureFlag('routeLibrary') === true;
  const routePlanningReleaseEnabled = useReleaseFeatureFlag('routePlanning') === true;
  const routeLibraryEnabled = !isEditing || routeLibraryReleaseEnabled;
  const routePlanningEnabled = !isEditing || routePlanningReleaseEnabled;
  const { session } = useSession();
  const insets = useSafeAreaInsets();
  const isOperator = managementLevel === 'operator';
  const isPersonalEvent = personalEvent || event?.tenant_id === null;
  const editorTitle = eventEditorTitle(isEditing, isOperator, isPersonalEvent);
  const [scheduleDefaults] = React.useState(() => createEventScheduleDefaults(getCurrentTime()));

  const [title, setTitle] = React.useState(event?.title ?? '');
  const [description, setDescription] = React.useState(event?.description ?? '');
  const [startsAt, setStartsAt] = React.useState(
    event ? new Date(event.starts_at) : scheduleDefaults.startsAt
  );
  const [endsAt, setEndsAt] = React.useState<Date>(
    event?.ends_at ? new Date(event.ends_at) : scheduleDefaults.endsAt
  );
  const [capacity, setCapacity] = React.useState(
    event?.capacity == null ? '' : String(event.capacity)
  );
  const [registrationPriceThb, setRegistrationPriceThb] = React.useState(
    event?.registration_price_thb ? String(event.registration_price_thb) : ''
  );
  const [paymentMethods, setPaymentMethods] = React.useState<EventPaymentMethod[]>(
    event?.registration_price_thb ? ['thai_qr'] : []
  );
  const [clubTransferDetails, setClubTransferDetails] = React.useState('');
  const [clubThaiQrStoragePath, setClubThaiQrStoragePath] = React.useState('');
  const [pendingClubThaiQr, setPendingClubThaiQr] =
    React.useState<ImagePicker.ImagePickerAsset | null>(null);
  const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = React.useState(
    isOperator && Boolean(tenantId)
  );
  const [checkInMode, setCheckInMode] = React.useState<EventCheckInMode>(
    event?.checkin_mode ?? 'none'
  );
  const [status, setStatus] = React.useState<EventStatus>(event?.status ?? 'draft');
  // Live attendee count for the operations row: starts from the stored count and
  // updates once the Attendees sheet loads the real roster (which includes
  // free-event walk-ins checked in without a registration).
  const [attendeeCount, setAttendeeCount] = React.useState(event?.attendee_count ?? 0);
  const [place, setPlace] = React.useState<PlaceSelection | null>(toPlaceSelection(event));
  const [routeVersionId, setRouteVersionId] = React.useState<string | null>(
    initialRouteVersionId ?? event?.route_version_id ?? null
  );
  const [routeName, setRouteName] = React.useState<string | null>(null);
  const [registrationFormSchema, setRegistrationFormSchema] =
    React.useState<RegistrationFormSchema>(EMPTY_REGISTRATION_FORM_SCHEMA);
  const [isLoadingRegistrationForm, setIsLoadingRegistrationForm] = React.useState(
    isOperator && Boolean(event)
  );
  const [eventLinks, setEventLinks] = React.useState<EventLinkDraft[]>([]);
  const [existingMedia, setExistingMedia] = React.useState<EventMedia[]>([]);
  const [removedMedia, setRemovedMedia] = React.useState<EventMedia[]>([]);
  const [pendingMediaAssets, setPendingMediaAssets] = React.useState<
    ImagePicker.ImagePickerAsset[]
  >([]);
  const [isLoadingAssets, setIsLoadingAssets] = React.useState(isOperator && Boolean(event));
  const [editActivity, setEditActivity] = React.useState<EventEditActivitySummary>(
    initialEditActivity ?? {
      registrationCount: event?.attendee_count ?? 0,
      rsvpCount: 0,
    }
  );
  const [isLoadingEditActivity, setIsLoadingEditActivity] = React.useState(
    isOperator && Boolean(event) && !initialEditActivity
  );
  const [activeSheet, setActiveSheet] = React.useState<EventEditorSheetId | null>(
    initialSheet === 'route' ? null : initialSheet
  );
  const [routeFlow, dispatchRouteFlow] = React.useReducer(
    eventRouteFlowReducer,
    initialSheet === 'route' ? 'library' : 'closed'
  );
  const [isRoutePlannerFullscreen, setIsRoutePlannerFullscreen] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [validationField, setValidationField] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(
    Boolean(initialRouteVersionId && initialRouteVersionId !== event?.route_version_id)
  );
  const [pendingSavedEvent, setPendingSavedEvent] = React.useState<Event | null>(null);
  const [pendingNotificationReasons, setPendingNotificationReasons] = React.useState<
    EventUpdateReason[]
  >([]);
  const titleRef = React.useRef<TextInput>(null);
  const descriptionRef = React.useRef<TextInput>(null);
  const createdEventIdRef = React.useRef<string | null>(null);
  const saveEventRef = React.useRef<(intent?: EventEditorSaveIntent) => Promise<void>>(
    async () => {}
  );
  const previousTenantIdRef = React.useRef(tenantId);

  const markDirty = React.useCallback(() => setIsDirty(true), []);

  React.useEffect(() => {
    if (previousTenantIdRef.current === tenantId) return;
    previousTenantIdRef.current = tenantId;
    setRouteVersionId(null);
    setRouteName(null);
  }, [tenantId]);

  React.useEffect(() => {
    let active = true;
    async function loadSelectedRoute() {
      if (!routeVersionId) {
        setRouteName(null);
        return;
      }
      try {
        const item = await fetchRouteVersion(routeVersionId);
        if (active) setRouteName(item?.route.name ?? null);
      } catch (routeError) {
        if (active) {
          setError(
            routeError instanceof Error ? routeError.message : 'Could not load the event route.'
          );
        }
      }
    }
    void loadSelectedRoute();
    return () => {
      active = false;
    };
  }, [routeVersionId]);

  function selectRoute(item: RouteLibraryItem | null) {
    markDirty();
    setRouteVersionId(item?.version.id ?? null);
    setRouteName(item?.route.name ?? null);
    if (item) dispatchRouteFlow({ type: 'close' });
  }

  async function attachPlannedRoute(nextRouteVersionId: string) {
    markDirty();
    setRouteVersionId(nextRouteVersionId);
    try {
      const item = await fetchRouteVersion(nextRouteVersionId);
      setRouteName(item?.route.name ?? 'New route');
    } catch {
      setRouteName('New route');
    } finally {
      dispatchRouteFlow({ type: 'close' });
    }
  }

  function requestRoutePlanner() {
    setIsRoutePlannerFullscreen(true);
    dispatchRouteFlow({ type: 'start-planning' });
  }

  React.useEffect(() => {
    let active = true;
    async function loadPaymentDetails() {
      setPendingClubThaiQr(null);
      if (!eventPaymentsEnabled || !isOperator || !tenantId) {
        setClubThaiQrStoragePath('');
        setClubTransferDetails('');
        setIsLoadingPaymentDetails(false);
        return;
      }
      setIsLoadingPaymentDetails(true);
      const { data, error: tenantError } = await supabase
        .from('tenants')
        .select('payment_transfer_details, thai_qr_storage_path')
        .eq('id', tenantId)
        .single();
      if (!active) return;
      if (tenantError) setError(tenantError.message);
      setClubThaiQrStoragePath(data?.thai_qr_storage_path ?? '');
      setClubTransferDetails(data?.payment_transfer_details ?? '');
      setIsLoadingPaymentDetails(false);
    }
    void loadPaymentDetails();
    return () => {
      active = false;
    };
  }, [eventPaymentsEnabled, isOperator, tenantId]);

  React.useEffect(() => {
    let active = true;
    async function loadRegistrationForm() {
      if (!registrationFormsEnabled || !isOperator || !event?.id) {
        setRegistrationFormSchema(EMPTY_REGISTRATION_FORM_SCHEMA);
        setIsLoadingRegistrationForm(false);
        return;
      }
      setIsLoadingRegistrationForm(true);
      const result = await supabase
        .from('event_registration_forms')
        .select('enabled, schema')
        .eq('event_id', event.id)
        .maybeSingle();
      if (!active) return;
      if (result.error) setError(result.error.message);
      else {
        setRegistrationFormSchema(
          result.data?.enabled
            ? normalizeRegistrationFormSchema(result.data.schema)
            : EMPTY_REGISTRATION_FORM_SCHEMA
        );
      }
      setIsLoadingRegistrationForm(false);
    }
    void loadRegistrationForm();
    return () => {
      active = false;
    };
  }, [event?.id, isOperator, registrationFormsEnabled]);

  React.useEffect(() => {
    let active = true;
    async function loadAssets() {
      setPendingMediaAssets([]);
      setRemovedMedia([]);
      if (!isOperator || !event?.id) {
        setEventLinks([]);
        setExistingMedia([]);
        setIsLoadingAssets(false);
        return;
      }
      setIsLoadingAssets(true);
      try {
        const assets = await fetchEventAssets(event.id);
        if (!active) return;
        setEventLinks(
          assets.links.map((link) => ({
            id: link.id,
            label: link.label,
            sortOrder: link.sort_order,
            url: link.url,
          }))
        );
        setExistingMedia(assets.media);
      } catch (assetError) {
        if (active) {
          setError(
            assetError instanceof Error ? assetError.message : 'Could not load event media.'
          );
        }
      } finally {
        if (active) setIsLoadingAssets(false);
      }
    }
    void loadAssets();
    return () => {
      active = false;
    };
  }, [event?.id, isOperator]);

  React.useEffect(() => {
    let active = true;
    async function loadEditActivity() {
      if (!isOperator || !event?.id) {
        setEditActivity(EMPTY_EVENT_EDIT_ACTIVITY);
        setIsLoadingEditActivity(false);
        return;
      }
      if (initialEditActivity) {
        setEditActivity(initialEditActivity);
        setIsLoadingEditActivity(false);
        return;
      }
      setIsLoadingEditActivity(true);
      const [registrations, rsvps] = await Promise.all([
        supabase
          .from('event_registrations')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', event.id)
          .in('status', ['pending_payment', 'confirmed']),
        supabase
          .from('event_rsvps')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', event.id)
          .eq('status', 'going'),
      ]);
      if (!active) return;
      if (registrations.error || rsvps.error) {
        setEditActivity({ registrationCount: event.attendee_count, rsvpCount: 0 });
        setError(registrations.error?.message ?? rsvps.error?.message ?? null);
      } else {
        setEditActivity({
          registrationCount: registrations.count ?? event.attendee_count,
          rsvpCount: rsvps.count ?? 0,
        });
      }
      setIsLoadingEditActivity(false);
    }
    void loadEditActivity();
    return () => {
      active = false;
    };
  }, [event?.attendee_count, event?.id, initialEditActivity, isOperator]);

  const draft = React.useMemo<EventFormDraft>(
    () => ({
      capacity,
      description,
      endsAt,
      eventLinks,
      mediaItemCount: existingMedia.length + pendingMediaAssets.length,
      paymentMethods,
      pendingMediaAssets,
      place,
      registrationFormSchema,
      registrationPriceThb,
      startsAt,
      tenantId,
      title,
      thaiQrPendingAsset: pendingClubThaiQr,
      thaiQrStoragePath: clubThaiQrStoragePath,
      transferDetails: clubTransferDetails,
    }),
    [
      capacity,
      description,
      endsAt,
      eventLinks,
      existingMedia.length,
      paymentMethods,
      pendingMediaAssets,
      place,
      registrationFormSchema,
      registrationPriceThb,
      startsAt,
      tenantId,
      title,
      pendingClubThaiQr,
      clubThaiQrStoragePath,
      clubTransferDetails,
    ]
  );
  const liveValidation = React.useMemo(
    () =>
      validateEventFormDraft(draft, eventPaymentsEnabled, eventDescriptionEnabled, isPersonalEvent),
    [draft, eventDescriptionEnabled, eventPaymentsEnabled, isPersonalEvent]
  );
  const canSave =
    liveValidation.valid &&
    !isLoadingAssets &&
    !isLoadingEditActivity &&
    !isLoadingRegistrationForm &&
    !isLoadingPaymentDetails &&
    !isSubmitting;

  const leave = React.useCallback(() => {
    if (onClose) onClose();
    else router.back();
  }, [onClose]);

  const requestClose = React.useCallback(() => {
    if (!isDirty || isSubmitting) {
      leave();
      return;
    }
    const discard = () => {
      setIsDirty(false);
      requestAnimationFrame(leave);
    };
    if (Platform.OS === 'web') {
      if (globalThis.confirm?.('Discard your unsaved event changes?')) discard();
      return;
    }
    const actions: Parameters<typeof Alert.alert>[2] = [
      { text: 'Keep editing', style: 'cancel' },
      { text: 'Discard', style: 'destructive', onPress: discard },
    ];
    // Offer a save action whenever the form is valid. When editing, saving keeps
    // the event's current draft/published status (the status toggle), so this is
    // also how a draft is moved to published — set the toggle, then Save. When
    // creating, a save from here lands as a draft, matching the create flow.
    if (liveValidation.valid && (isEditing || eventDraftPublishEnabled)) {
      actions.splice(1, 0, {
        text: isEditing ? 'Save' : 'Save draft',
        onPress: () => void saveEventRef.current(isEditing ? undefined : 'draft'),
      });
    }
    Alert.alert('Unsaved changes', 'Keep editing or discard these changes.', actions);
  }, [eventDraftPublishEnabled, isDirty, isEditing, isSubmitting, leave, liveValidation.valid]);

  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      if (!isDirty) return false;
      requestClose();
      return true;
    });
    return () => subscription.remove();
  }, [isDirty, requestClose]);

  function showValidationError(field: string, message: string) {
    setError(message);
    setValidationField(field);
    const sheet = editorSheetForValidationField(field);
    if (sheet) setActiveSheet(sheet);
    if (field === 'title') requestAnimationFrame(() => titleRef.current?.focus());
    if (field === 'description') requestAnimationFrame(() => descriptionRef.current?.focus());
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async function saveEvent(intent: EventEditorSaveIntent = 'published') {
    setError(null);
    setValidationField(null);
    if (isLoadingAssets || isLoadingEditActivity || isLoadingRegistrationForm) {
      setError('Event settings are still loading.');
      return;
    }
    const validation = validateEventFormDraft(
      draft,
      eventPaymentsEnabled,
      eventDescriptionEnabled,
      isPersonalEvent
    );
    if (!validation.valid) {
      if (__DEV__) {
        console.warn('[event editor] publish blocked by validation', {
          field: validation.field,
          message: validation.error,
        });
      }
      showValidationError(validation.field, validation.error);
      return;
    }

    setIsSubmitting(true);
    const nextStatus: EventStatus = isEditing ? status : intent;
    let nextThaiQrStoragePath = clubThaiQrStoragePath;
    try {
      if (isOperator && pendingClubThaiQr && validation.tenantId) {
        const extension = eventMediaExtension({
          mediaKind: pendingClubThaiQr.type,
          name: pendingClubThaiQr.fileName,
          type: pendingClubThaiQr.mimeType,
        });
        nextThaiQrStoragePath = `${validation.tenantId}/thai-qr.${extension}`;
        await uploadLocalFileToStorage({
          bucket: 'club-payment-qr',
          contentType: pendingClubThaiQr.mimeType ?? 'image/jpeg',
          storagePath: nextThaiQrStoragePath,
          upsert: true,
          uri: pendingClubThaiQr.uri,
        });
      }
      if (isOperator && validation.tenantId && validation.parsedRegistrationPrice > 0) {
        const tenantResult = await supabase
          .from('tenants')
          .update({
            payment_transfer_details: clubTransferDetails.trim(),
            thai_qr_storage_path: nextThaiQrStoragePath,
          })
          .eq('id', validation.tenantId);
        if (tenantResult.error) throw tenantResult.error;
      }
    } catch (paymentError) {
      setIsSubmitting(false);
      setError(
        paymentError instanceof Error
          ? paymentError.message
          : 'Could not save club payment details.'
      );
      setActiveSheet('registration');
      return;
    }
    const payload = {
      capacity: validation.parsedCapacity,
      checkin_mode: isOperator ? checkInMode : ('none' as const),
      description: validation.normalizedDescription,
      ends_at: validation.endsAt.toISOString(),
      payment_methods:
        isOperator && validation.parsedRegistrationPrice > 0
          ? (['thai_qr'] as EventPaymentMethod[])
          : [],
      place_address: validation.place.address,
      place_id: validation.place.placeId,
      place_lat: validation.place.lat,
      place_lng: validation.place.lng,
      place_name: validation.place.name,
      registration_closes_at: validation.endsAt.toISOString(),
      registration_price_thb: isOperator ? validation.parsedRegistrationPrice : 0,
      revenuecat_offering_id: null,
      revenuecat_package_id: null,
      starts_at: startsAt.toISOString(),
      route_version_id: routeVersionId,
      status: nextStatus,
      tenant_id: validation.tenantId,
      thai_qr_merchant_ref:
        isOperator && validation.parsedRegistrationPrice > 0 ? clubTransferDetails.trim() : null,
      title: validation.normalizedTitle,
    };

    if (event && isOperator) {
      const editError = validateEventPostRegistrationEdit(event, payload, editActivity);
      if (editError) {
        setIsSubmitting(false);
        setError(editError);
        setActiveSheet('registration');
        return;
      }
    }

    const persistedEventId = event?.id ?? createdEventIdRef.current;
    const result = persistedEventId
      ? await supabase
          .from('events')
          .update({ ...payload, updated_by: session?.user.id ?? null })
          .eq('id', persistedEventId)
          .select('*')
          .single()
      : await supabase.from('events').insert(payload).select('*').single();
    if (result.error) {
      setIsSubmitting(false);
      setError(eventSaveErrorMessage(result.error.message));
      return;
    }
    if (!event) createdEventIdRef.current = result.data.id;

    const formEnabled =
      registrationFormsEnabled &&
      isOperator &&
      registrationFormHasQuestions(validation.normalizedRegistrationForm);
    if (
      registrationFormsEnabled &&
      isOperator &&
      validation.tenantId &&
      (formEnabled || isEditing)
    ) {
      const formResult = await supabase.from('event_registration_forms').upsert(
        {
          created_by: session?.user.id ?? null,
          enabled: formEnabled,
          event_id: result.data.id,
          schema: registrationFormSchemaToJson(validation.normalizedRegistrationForm),
          tenant_id: validation.tenantId,
        },
        { onConflict: 'event_id' }
      );
      if (formResult.error) {
        setIsSubmitting(false);
        setError(formResult.error.message);
        return;
      }
    }

    if (isOperator && validation.tenantId) {
      try {
        await saveEventFormAssets({
          actorId: session?.user.id ?? null,
          eventId: result.data.id,
          existingMediaCount: existingMedia.length,
          links: validation.normalizedEventLinks,
          pendingMediaAssets,
          removedMedia,
          tenantId: validation.tenantId,
          title: validation.normalizedTitle,
        });
      } catch (assetError) {
        setIsSubmitting(false);
        setError(assetError instanceof Error ? assetError.message : 'Could not save event media.');
        return;
      }
    }

    const eventProperties = {
      event_status: result.data.status,
      has_registration_form: formEnabled,
      is_paid: validation.parsedRegistrationPrice > 0,
    };
    if (event) {
      posthog.capture('event_updated', eventProperties);
    } else {
      posthog.capture('event_created', eventProperties);
    }
    setIsSubmitting(false);
    setIsDirty(false);
    invalidateEventData();
    const notificationReasons = event ? eventUpdateReasons(event, result.data) : [];
    if (shouldPromptForEventUpdate(isEditing, isOperator)) {
      setPendingSavedEvent(result.data);
      setPendingNotificationReasons(notificationReasons);
      setActiveSheet('notifications');
      return;
    }
    onSaved(result.data, { notificationReasons });
  }

  React.useEffect(() => {
    saveEventRef.current = saveEvent;
  });

  function finishPendingNotification() {
    if (!pendingSavedEvent) {
      setActiveSheet(null);
      return;
    }
    const saved = pendingSavedEvent;
    const reasons = pendingNotificationReasons;
    setPendingSavedEvent(null);
    setPendingNotificationReasons([]);
    setActiveSheet(null);
    onSaved(saved, { notificationReasons: reasons });
  }

  function addEventLink() {
    markDirty();
    setEventLinks((current) =>
      current.length >= MAX_EVENT_LINKS ? current : [...current, { label: '', url: '' }]
    );
  }

  function updateEventLink(index: number, patch: Partial<EventLinkDraft>) {
    markDirty();
    setEventLinks((current) =>
      current.map((link, currentIndex) => (currentIndex === index ? { ...link, ...patch } : link))
    );
  }

  function normalizeEventLink(index: number) {
    const link = eventLinks[index];
    if (!link) return;
    const url = normalizeEventLinkUrl(link.url);
    if (!url) return;
    updateEventLink(index, {
      label: link.label.trim() || eventLinkLabelSuggestion(url),
      url,
    });
  }

  function removeEventLink(index: number) {
    markDirty();
    setEventLinks((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function removeExistingMedia(media: EventMedia) {
    markDirty();
    setExistingMedia((current) => current.filter((item) => item.id !== media.id));
    setRemovedMedia((current) => [...current, media]);
  }

  function removePendingMedia(index: number) {
    markDirty();
    setPendingMediaAssets((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  async function pickEventMedia() {
    setError(null);
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError('Allow photo library access to add event photos or videos.');
      return;
    }
    const remainingSlots = MAX_EVENT_MEDIA_ITEMS - existingMedia.length - pendingMediaAssets.length;
    if (remainingSlots <= 0) {
      setError(`Add up to ${MAX_EVENT_MEDIA_ITEMS} photos or videos.`);
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      allowsMultipleSelection: true,
      mediaTypes: ['images', 'videos'],
      quality: 0.9,
      selectionLimit: remainingSlots,
      videoMaxDuration: 60,
    });
    if (result.canceled) return;
    if (result.assets.length > remainingSlots) {
      setError(`Add up to ${MAX_EVENT_MEDIA_ITEMS} photos or videos.`);
      return;
    }
    for (const asset of result.assets) {
      const uploadError = eventMediaUploadError({
        duration: asset.duration == null ? null : asset.duration / 1000,
        mediaKind: asset.type,
        name: asset.fileName,
        size: asset.fileSize,
        type: asset.mimeType,
      });
      if (uploadError) {
        setError(uploadError);
        return;
      }
    }
    markDirty();
    setPendingMediaAssets((current) => [...current, ...result.assets]);
  }

  async function pickClubThaiQr() {
    setError(null);
    setValidationField(null);
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError('Allow photo library access to upload the club Thai payment QR code.');
      setValidationField('thaiQr');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 1,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    const uploadError = eventMediaUploadError({
      mediaKind: asset.type,
      name: asset.fileName,
      size: asset.fileSize,
      type: asset.mimeType,
    });
    if (uploadError) {
      setError(uploadError);
      setValidationField('thaiQr');
      return;
    }
    markDirty();
    setPendingClubThaiQr(asset);
  }

  async function cancelEvent() {
    if (!event) return;
    setIsCancelling(true);
    const result = await supabase
      .from('events')
      .update({ status: 'cancelled', updated_by: session?.user.id ?? null })
      .eq('id', event.id)
      .select('*')
      .single();
    setIsCancelling(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setActiveSheet(null);
    onSaved(result.data, { notificationReasons: [] });
  }

  const hasEditActivity = isEditing && eventHasEditActivity(editActivity);
  const paymentSettingsLocked = hasEditActivity;
  const registrationSummary = [
    capacity.trim() ? `${capacity.trim()} spots` : 'No capacity limit',
    registrationPriceThb.trim() && Number(registrationPriceThb) > 0
      ? `${registrationPriceThb.trim()} THB`
      : 'Free',
  ].join(' · ');
  const questionCount = registrationFormSchema.fields.length;
  let questionSummary = 'None';
  if (questionCount === 1) questionSummary = '1 question';
  if (questionCount > 1) questionSummary = `${questionCount} questions`;
  const contentCount = existingMedia.length + pendingMediaAssets.length;
  const capacityInlineError =
    capacity.trim() && Number(capacity) < 1 ? 'Capacity must be at least 1.' : null;
  const priceNumber = registrationPriceThb.trim() ? Number(registrationPriceThb) : 0;
  const priceInlineError = !isValidEventPriceThb(priceNumber)
    ? `Price must be a whole THB amount up to ${MAX_EVENT_PRICE_THB}.`
    : null;
  const paymentInlineError = paidEventDetailsError(priceNumber, {
    thaiQrStoragePath: clubThaiQrStoragePath || pendingClubThaiQr?.uri || '',
    transferDetails: clubTransferDetails,
  });
  let checkInSummary = 'Member chain check-in';
  if (checkInMode === 'none') checkInSummary = 'No check-in';
  if (checkInMode === 'admin_only') checkInSummary = 'Admin check-in';
  const completedLinkCount = eventLinks.filter((link) => normalizeEventLinkUrl(link.url)).length;
  let eventLinksSummary = 'None';
  if (eventLinks.length > 0 && completedLinkCount === 0) eventLinksSummary = 'Adding a link';
  if (completedLinkCount === 1) eventLinksSummary = '1 link';
  if (completedLinkCount > 1) eventLinksSummary = `${completedLinkCount} links`;
  // Both create and edit now show a sticky footer action, so reserve the same
  // space beneath the scroll content in either mode.
  const editorFooterHeight = 96 + insets.bottom;

  let editSaveLabel = 'Save changes';
  if (isSubmitting) {
    editSaveLabel = 'Saving…';
  } else if (!isDirty) {
    editSaveLabel = 'No changes to save';
  }
  const displaySchedule = formatEditorSchedule(startsAt, endsAt);
  let visibilitySummary = 'Draft';
  if (status === 'published') visibilitySummary = 'Published';
  if (status === 'cancelled') visibilitySummary = 'Cancelled';
  let publishButtonLabel = isOperator ? 'Publish' : 'Publish run';
  if (isSubmitting) publishButtonLabel = 'Publishing…';

  function setSheet(next: EventEditorSheetId) {
    setError(null);
    setValidationField(null);
    if (next === 'route') {
      setActiveSheet(null);
      dispatchRouteFlow({ type: 'open-library' });
      return;
    }
    setActiveSheet(next);
  }

  function dismissSheet(next: EventEditorSheetId) {
    const validationError = sheetValidationError(next, {
      capacityError: capacityInlineError,
      contentCount,
      endsAt,
      eventLinks,
      hasThaiQr: Boolean(clubThaiQrStoragePath || pendingClubThaiQr),
      paymentError: paymentInlineError,
      pendingMediaAssets,
      place,
      priceError: priceInlineError,
      registrationFormSchema,
      startsAt,
      validationField,
    });

    if (validationError) {
      setError(validationError.message);
      setValidationField(validationError.field);
      return;
    }

    setError(null);
    setValidationField(null);
    setActiveSheet(null);
  }

  let editorCommunityControl: React.ReactNode;
  if (isPersonalEvent) {
    editorCommunityControl = (
      <Text className="font-bold" numberOfLines={1}>
        Personal event
      </Text>
    );
  } else if (isEditing) {
    editorCommunityControl = (
      <View accessibilityRole="button" accessibilityState={{ disabled: true }}>
        <Text className="font-bold" numberOfLines={1}>
          {tenantName ?? 'Community'}
        </Text>
      </View>
    );
  } else if (Platform.OS !== 'web') {
    editorCommunityControl = (
      <Pressable
        accessibilityLabel={tenantName ?? 'Select community'}
        accessibilityRole="button"
        className="min-h-11 flex-row items-center gap-1 self-start"
        onPress={() => {
          const currentIndex = communities.findIndex((community) => community.id === tenantId);
          const nextCommunity = communities[(currentIndex + 1) % communities.length];
          if (!nextCommunity) return;
          markDirty();
          onTenantChange?.(nextCommunity.id);
        }}
      >
        <Text className="font-bold" numberOfLines={1}>
          {tenantName ?? 'Select community'}
        </Text>
        <Icon as={ChevronDownIcon} className="size-4" />
      </Pressable>
    );
  } else {
    editorCommunityControl = (
      <select
        aria-label="Select community"
        value={tenantId ?? ''}
        onChange={(eventTarget) => {
          markDirty();
          onTenantChange?.(eventTarget.target.value);
        }}
        style={{
          minHeight: 44,
          maxWidth: 260,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          fontWeight: 700,
        }}
      >
        <option value="">Select community</option>
        {communities.map((community) => (
          <option key={community.id} value={community.id}>
            {community.name}
          </option>
        ))}
      </select>
    );
  }

  let routeCreatorAction: React.ReactNode = null;
  if (routePlanningEnabled && !event && !isPersonalEvent) {
    routeCreatorAction = (
      <View className="gap-3">
        <Button className="w-full" onPress={requestRoutePlanner} variant="secondary">
          <Text className="flex-1 text-center">Create route</Text>
        </Button>
        <Text variant="muted">
          Plan the course now. Saving the route brings it back and attaches it to this event draft.
        </Text>
      </View>
    );
  } else if (routePlanningEnabled && event && tenantSlug) {
    routeCreatorAction = (
      <View className="gap-3">
        {isDirty ? (
          <Button className="w-full" disabled variant="secondary">
            <Text className="flex-1 text-center">Create route</Text>
          </Button>
        ) : (
          <Link
            href={{
              pathname: '/app/routes/new',
              params: {
                editor: isOperator ? 'operator' : 'member',
                event: event.id,
                tenant: tenantSlug,
              },
            }}
            asChild
          >
            <Button className="w-full" variant="secondary">
              <Text className="flex-1 text-center">Create route</Text>
            </Button>
          </Link>
        )}
        {isDirty ? (
          <Text variant="muted">Save your event changes before opening the builder.</Text>
        ) : (
          <Text variant="muted">
            The saved route returns here ready to publish with this event.
          </Text>
        )}
      </View>
    );
  }

  const editorHeader = (
    <View
      className="min-h-16 flex-row items-center gap-3 border-b border-border px-5"
      style={{
        paddingTop: !embedded && Platform.OS !== 'web' ? insets.top : 0,
      }}
    >
      <Pressable
        accessibilityLabel={`${isEditing ? 'Back from' : 'Close'} ${
          isOperator ? 'event' : 'run'
        } editor`}
        className="size-11 items-center justify-center rounded-full active:bg-muted"
        onPress={requestClose}
      >
        <Icon as={isEditing ? ChevronLeftIcon : XIcon} className="size-6" />
      </Pressable>
      <View className="min-w-0 flex-1">{editorCommunityControl}</View>
      {isEditing && isDirty ? (
        <Button disabled={!canSave || !isDirty} onPress={() => void saveEvent()} size="sm">
          <Text>{isSubmitting ? 'Saving…' : 'Save'}</Text>
        </Button>
      ) : null}
    </View>
  );

  return (
    <View className="flex-1 bg-background">
      {!embedded ? (
        <Stack.Screen
          options={{
            gestureEnabled: !isDirty,
            headerShown: false,
            title: editorTitle,
          }}
        />
      ) : null}

      {editorHeader}

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        className="flex-1"
      >
        <ScrollView
          className="flex-1 bg-background"
          contentContainerStyle={{ paddingBottom: editorFooterHeight }}
          contentInsetAdjustmentBehavior="automatic"
          keyboardShouldPersistTaps="handled"
        >
          <View className="mx-auto w-full max-w-3xl px-5 pb-8 pt-5">
            {!tenantId && !isPersonalEvent ? (
              <Text className="mb-3 text-sm text-destructive">
                Select a community before saving this event.
              </Text>
            ) : null}

            <TextInput
              ref={titleRef}
              accessibilityLabel="Event title"
              aria-invalid={validationField === 'title'}
              className="min-h-14 font-sans text-xl font-bold leading-6 text-foreground outline-none"
              maxLength={MAX_EVENT_TITLE_CHARACTERS}
              multiline
              onChangeText={(value) => {
                markDirty();
                setTitle(value);
              }}
              onBlur={() => {
                const normalizedTitle = title.trim();
                if (normalizedTitle === title) return;
                markDirty();
                setTitle(normalizedTitle);
              }}
              placeholder={isOperator ? 'Event title' : 'Run title'}
              placeholderTextColor="#71717A"
              value={title}
            />
            <Text className="self-end font-mono text-xs text-muted-foreground">
              {title.length}/{MAX_EVENT_TITLE_CHARACTERS}
            </Text>
            {validationField === 'title' && error ? (
              <Text className="text-sm text-destructive">{error}</Text>
            ) : null}

            {/*
              Existing events keep their description on screen even when the
              field is gated, so editing one written earlier does not silently
              drop it — the value is still submitted either way.
            */}
            {eventDescriptionEnabled || description.trim() ? (
              <>
                <TextInput
                  ref={descriptionRef}
                  accessibilityLabel="Event description"
                  aria-invalid={validationField === 'description'}
                  className="min-h-36 py-4 font-body text-lg leading-7 text-foreground outline-none"
                  editable={eventDescriptionEnabled}
                  maxLength={MAX_EVENT_DESCRIPTION_CHARACTERS}
                  multiline
                  onChangeText={(value) => {
                    markDirty();
                    setDescription(value);
                  }}
                  onBlur={() => {
                    const normalizedDescription = description.trim();
                    if (normalizedDescription === description) return;
                    markDirty();
                    setDescription(normalizedDescription);
                  }}
                  placeholder="What should attendees know?"
                  placeholderTextColor="#71717A"
                  textAlignVertical="top"
                  value={description}
                />
                {eventDescriptionEnabled ? (
                  <Text className="self-end font-mono text-xs text-muted-foreground">
                    {description.length}/{MAX_EVENT_DESCRIPTION_CHARACTERS}
                  </Text>
                ) : null}
                {validationField === 'description' && error ? (
                  <Text className="pb-3 text-sm text-destructive">{error}</Text>
                ) : null}
              </>
            ) : null}

            <View className="border-t border-border">
              <EventEditorRow
                icon={CalendarClockIcon}
                label="Schedule"
                onPress={() => setSheet('schedule')}
                value={displaySchedule}
              />
              <EventEditorRow
                icon={MapPinIcon}
                label="Location"
                onClear={
                  place
                    ? () => {
                        markDirty();
                        setPlace(null);
                      }
                    : undefined
                }
                onPress={() => setSheet('location')}
                value={place ? `${place.name} · ${place.address}` : 'Add a location'}
              />
              {routeLibraryEnabled ? (
                <EventEditorRow
                  icon={RouteIcon}
                  label="Route"
                  onClear={
                    routeVersionId
                      ? () => {
                          markDirty();
                          setRouteVersionId(null);
                          setRouteName(null);
                        }
                      : undefined
                  }
                  onPress={() => setSheet('route')}
                  value={routeName ?? (routeVersionId ? 'Loading route…' : 'Add a route')}
                />
              ) : null}
              {isOperator ? (
                <>
                  <EventEditorRow
                    icon={TicketIcon}
                    label="Registration"
                    onPress={() => setSheet('registration')}
                    value={registrationSummary}
                  />
                  {registrationFormsEnabled ? (
                    <EventEditorRow
                      icon={ListChecksIcon}
                      label="Attendee questions"
                      onPress={() => setSheet('questions')}
                      value={questionSummary}
                    />
                  ) : null}
                  {eventMediaEnabled ? (
                    <EventEditorRow
                      icon={ImagesIcon}
                      label="Photos and videos"
                      onPress={() => setSheet('media')}
                      value={contentCount ? `${contentCount} selected` : 'None'}
                    />
                  ) : null}
                  <EventEditorRow
                    icon={LinkIcon}
                    label="Additional links"
                    onPress={() => setSheet('links')}
                    value={eventLinksSummary}
                  />
                  <EventEditorRow
                    icon={ScanLineIcon}
                    label="Check-in"
                    onPress={() => setSheet('checkin')}
                    value={checkInSummary}
                  />
                </>
              ) : null}
              {isEditing && eventDraftPublishEnabled ? (
                <EventEditorRow
                  icon={EyeIcon}
                  label="Visibility"
                  onPress={() => setSheet('visibility')}
                  value={visibilitySummary}
                />
              ) : null}
            </View>

            {isEditing && event && isOperator ? (
              <View className="mt-8">
                <Text variant="label" className="py-4 text-muted-foreground">
                  Event operations
                </Text>
                <EventEditorRow
                  icon={UsersIcon}
                  label="Attendees"
                  onPress={() => setSheet('attendees')}
                  value={`${attendeeCount} going`}
                />
                {eventPaymentsEnabled && paymentManagementEnabled && paymentReviewEnabled ? (
                  <EventEditorRow
                    icon={CreditCardIcon}
                    label="Payment review"
                    onPress={() => setSheet('payments')}
                    value="Review registrations and receipts"
                  />
                ) : null}
                <EventEditorRow
                  icon={BellIcon}
                  label="Notify attendees"
                  onPress={() => setSheet('notifications')}
                  value="Send an event update"
                />
                {event.status !== 'cancelled' ? (
                  <EventEditorRow
                    destructive
                    icon={BanIcon}
                    label="Cancel event"
                    onPress={() => setSheet('cancel')}
                    value="Keep registrations for review"
                  />
                ) : null}
              </View>
            ) : null}

            {isEditing && event && !isOperator && event.status !== 'cancelled' ? (
              <View className="mt-8 border-t border-border">
                <Text variant="label" className="py-4 text-muted-foreground">
                  Your run
                </Text>
                <EventEditorRow
                  destructive
                  icon={BanIcon}
                  label="Cancel run"
                  onPress={() => setSheet('cancel')}
                  value="Remove it from upcoming runs"
                />
              </View>
            ) : null}

            {error && !validationField ? (
              <Text className="pt-5 text-sm text-destructive" selectable>
                {error}
              </Text>
            ) : null}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {!isEditing ? (
        <View
          className="absolute inset-x-0 bottom-0 border-t border-border bg-background px-5 pt-3"
          style={{ paddingBottom: Math.max(insets.bottom, 12) }}
        >
          <View className="mx-auto w-full max-w-3xl flex-row gap-3">
            <Button
              className="flex-1"
              disabled={isSubmitting || isLoadingAssets || (!isPersonalEvent && !tenantId)}
              onPress={() => void saveEvent('published')}
            >
              <Text>{publishButtonLabel}</Text>
            </Button>
            {/*
              Without drafts there are only two ways out of this screen, so both
              are on it: publish, or throw it away. requestClose already asks for
              confirmation when something has been typed.
            */}
            {eventDraftPublishEnabled ? (
              <Button
                className="flex-1"
                disabled={isSubmitting || isLoadingAssets || (!isPersonalEvent && !tenantId)}
                onPress={() => void saveEvent('draft')}
                variant="secondary"
              >
                <Text>{isSubmitting ? 'Saving…' : 'Save draft'}</Text>
              </Button>
            ) : (
              <Button
                className="flex-1"
                disabled={isSubmitting}
                onPress={requestClose}
                variant="secondary"
              >
                <Text>Cancel</Text>
              </Button>
            )}
          </View>
        </View>
      ) : null}

      {/*
        Editing shows a sticky confirm button so changes are saved with one
        obvious tap, instead of relying on the small header action or the
        back-out dialog. Disabled until something actually changed and the form
        is valid; saving keeps the event's current draft/published status.
      */}
      {isEditing ? (
        <View
          className="absolute inset-x-0 bottom-0 border-t border-border bg-background px-5 pt-3"
          style={{ paddingBottom: Math.max(insets.bottom, 12) }}
        >
          <View className="mx-auto w-full max-w-3xl">
            <Button
              accessibilityLabel="Save event changes"
              disabled={!canSave || !isDirty}
              onPress={() => void saveEvent()}
            >
              <Text>{editSaveLabel}</Text>
            </Button>
          </View>
        </View>
      ) : null}

      <EventEditorSheet
        error={activeSheet === 'schedule' && !validationField ? error : null}
        isPresented={activeSheet === 'schedule'}
        onDismiss={() => dismissSheet('schedule')}
        title="Schedule"
      >
        <View className="py-3">
          <Text variant="label" className="py-3 text-muted-foreground">
            Starts
          </Text>
          <DateTimeField
            label="Start date"
            mode="date"
            onChange={(calendarDate) => {
              markDirty();
              const nextStart = replaceCalendarDate(startsAt, calendarDate);
              setStartsAt(nextStart);
              setEndsAt(ensureValidEnd(nextStart, endsAt));
            }}
            value={startsAt}
          />
          <DateTimeField
            label="Start time"
            mode="time"
            onChange={(nextStart) => {
              markDirty();
              setStartsAt(nextStart);
              setEndsAt(ensureValidEnd(nextStart, endsAt));
            }}
            value={startsAt}
          />
          <Text variant="label" className="pb-3 pt-7 text-muted-foreground">
            Ends
          </Text>
          <DateTimeField
            label="End date"
            minimumDate={startsAt}
            mode="date"
            onChange={(calendarDate) => {
              markDirty();
              setEndsAt(replaceCalendarDate(endsAt, calendarDate));
            }}
            value={endsAt}
          />
          <DateTimeField
            error={eventScheduleError(startsAt, endsAt)}
            label="End time"
            minimumDate={startsAt}
            mode="time"
            onChange={(nextEnd) => {
              markDirty();
              setEndsAt(nextEnd);
            }}
            value={endsAt}
          />
          <Text variant="muted" className="pt-4">
            Registration closes at the event end. Start and end use the same date format.
          </Text>
        </View>
      </EventEditorSheet>

      {routeFlow !== 'closed' ? (
        <Modal
          animationType="slide"
          onRequestClose={() => dispatchRouteFlow({ type: 'close' })}
          presentationStyle="fullScreen"
          visible
        >
          {routeFlow === 'planner' && routePlanningEnabled && tenantId ? (
            <View className="flex-1 bg-background">
              <RoutePlanner
                draftKey={`route-planner-draft:${tenantId}:event-create`}
                exitLabel="Back to event"
                initialCenter={place ? { latitude: place.lat, longitude: place.lng } : undefined}
                isFullscreen={isRoutePlannerFullscreen}
                onExit={() => dispatchRouteFlow({ type: 'close' })}
                onFullscreenChange={setIsRoutePlannerFullscreen}
                onSaved={attachPlannedRoute}
                tenantId={tenantId}
                workspaceTitle={`Create route · ${tenantName ?? 'Event'}`}
              />
            </View>
          ) : (
            <View
              className="flex-1 bg-background px-4"
              style={{ paddingTop: Math.max(insets.top, 12) }}
            >
              <EventEditorSurface
                error={routeFlow === 'library' && !validationField ? error : null}
                onDismiss={() => dispatchRouteFlow({ type: 'close' })}
                title="Route"
              >
                {tenantId ? (
                  <View className="gap-6 pt-4">
                    <View className="gap-3">
                      <RouteMapPreview
                        geometry={SIXAM_MONDAY_ROUTE.geometry}
                        name={SIXAM_MONDAY_ROUTE.name}
                      />
                      <View className="flex-row items-end justify-between gap-4 px-1">
                        <View className="min-w-0 flex-1 gap-1">
                          <Text variant="label">6am default route</Text>
                          <Text variant="large" numberOfLines={1}>
                            Monday Run · Bangkok
                          </Text>
                        </View>
                        <Text variant="mono">
                          {formatRouteDistance(SIXAM_MONDAY_ROUTE.distanceM)}
                        </Text>
                      </View>
                    </View>
                    {routeCreatorAction}
                    <RouteSelector
                      initialItems={initialRoutes}
                      onSelect={selectRoute}
                      selectedVersionId={routeVersionId}
                      tenantId={tenantId}
                    />
                  </View>
                ) : (
                  <Text variant="muted" className="py-4">
                    Select a community before adding a route.
                  </Text>
                )}
              </EventEditorSurface>
            </View>
          )}
        </Modal>
      ) : null}

      <EventEditorSheet
        error={activeSheet === 'location' && !validationField ? error : null}
        isPresented={activeSheet === 'location'}
        onDismiss={() => dismissSheet('location')}
        title="Location"
      >
        {place ? (
          <View className="border-b border-border py-4">
            <Text variant="large">{place.name}</Text>
            <Text variant="muted">{place.address}</Text>
          </View>
        ) : null}
        <EventPlaceSearch
          onSelect={(nextPlace) => {
            markDirty();
            setPlace(nextPlace);
            setActiveSheet(null);
          }}
          validationError={validationField === 'place' ? error : null}
        />
      </EventEditorSheet>

      <EventEditorSheet
        error={activeSheet === 'registration' && !validationField ? error : null}
        isPresented={activeSheet === 'registration'}
        onDismiss={() => dismissSheet('registration')}
        title="Registration"
      >
        <View className="gap-5 py-4">
          {hasEditActivity ? (
            <View className="border-b border-border pb-4">
              <Text variant="label">Registration activity</Text>
              <Text variant="muted" className="pt-1">
                Capacity can only increase. Payment settings are locked after registrations or
                holds.
              </Text>
              <Text variant="mono" className="pt-2">
                {isLoadingEditActivity
                  ? 'Checking activity…'
                  : `${editActivity.registrationCount + editActivity.rsvpCount} records`}
              </Text>
            </View>
          ) : null}
          <View className="gap-1">
            <Text variant="label">Capacity</Text>
            <FlatField
              accessibilityLabel="Event capacity"
              keyboardType="number-pad"
              onChangeText={(value) => {
                markDirty();
                setCapacity(numericInputValue(value));
              }}
              placeholder="No capacity limit"
              value={capacity}
            />
            {capacityInlineError ? (
              <Text className="text-sm text-destructive" selectable>
                {capacityInlineError}
              </Text>
            ) : null}
          </View>
          {eventPaymentsEnabled ? (
            <View className="gap-1">
              <Text variant="label">Price in THB</Text>
              <FlatField
                accessibilityLabel="Registration price in THB"
                editable={!paymentSettingsLocked}
                keyboardType="number-pad"
                onChangeText={(value) => {
                  markDirty();
                  const numericValue = numericInputValue(value);
                  setRegistrationPriceThb(numericValue);
                  setPaymentMethods(Number(numericValue) > 0 ? ['thai_qr'] : []);
                }}
                placeholder="0 for free"
                value={registrationPriceThb}
              />
              {priceInlineError ? (
                <Text className="text-sm text-destructive" selectable>
                  {priceInlineError}
                </Text>
              ) : null}
            </View>
          ) : null}
          {priceNumber > 0 && eventPaymentsEnabled ? (
            <View className="gap-4 border-t border-border pt-4">
              <View className="gap-1">
                <Text variant="label">Thai payment QR</Text>
                <Text variant="muted">
                  Stored on the club and reused for future paid events. RevenueCat is not supported
                  yet.
                </Text>
              </View>
              <Pressable
                accessibilityLabel="Upload club Thai payment QR code"
                accessibilityRole="button"
                className="aspect-square w-40 items-center justify-center overflow-hidden rounded-md border border-border active:opacity-75"
                disabled={paymentSettingsLocked}
                onPress={() => void pickClubThaiQr()}
              >
                {pendingClubThaiQr || clubThaiQrStoragePath ? (
                  <Image
                    contentFit="cover"
                    source={{
                      uri:
                        pendingClubThaiQr?.uri ??
                        storagePublicUrl('club-payment-qr', clubThaiQrStoragePath),
                    }}
                    style={{ height: '100%', width: '100%' }}
                  />
                ) : (
                  <View className="items-center gap-2 p-3">
                    <Icon as={ImagesIcon} className="size-6 text-muted-foreground" />
                    <Text className="text-center text-sm">Upload QR image</Text>
                  </View>
                )}
              </Pressable>
              <View className="gap-1">
                <Text variant="label">Transfer details</Text>
                <TextInput
                  accessibilityLabel="Club bank transfer details"
                  className="min-h-24 border-b border-border py-2 font-body text-base text-foreground outline-none"
                  editable={!paymentSettingsLocked}
                  multiline
                  onChangeText={(value) => {
                    markDirty();
                    setClubTransferDetails(value);
                  }}
                  placeholder="Bank, account name, account number, and payment reference"
                  placeholderTextColor="#71717A"
                  textAlignVertical="top"
                  value={clubTransferDetails}
                />
              </View>
              {paymentInlineError ? (
                <Text className="text-sm text-destructive" selectable>
                  {paymentInlineError}
                </Text>
              ) : null}
            </View>
          ) : (
            <Text variant="muted">This event is free.</Text>
          )}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        error={activeSheet === 'questions' && !validationField ? error : null}
        isPresented={registrationFormsEnabled && activeSheet === 'questions'}
        onDismiss={() => dismissSheet('questions')}
        title="Attendee questions"
      >
        <View className="py-4">
          {isLoadingRegistrationForm ? (
            <ActivityIndicator />
          ) : (
            <EventRegistrationFormBuilder
              onChange={(schema) => {
                markDirty();
                setRegistrationFormSchema(schema);
              }}
              schema={registrationFormSchema}
            />
          )}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        error={activeSheet === 'media' && !validationField ? error : null}
        isPresented={eventMediaEnabled && activeSheet === 'media'}
        onDismiss={() => dismissSheet('media')}
        title="Photos and videos"
      >
        <View className="gap-3 py-4">
          <Text variant="muted">
            Add up to {MAX_EVENT_MEDIA_ITEMS} photos, or videos up to 60 seconds. Each file can be{' '}
            {formatEventMediaUploadLimit()}.
          </Text>
          {(eventMediaCountError(contentCount) ??
          pendingMediaValidationError(pendingMediaAssets)) ? (
            <Text className="text-sm text-destructive" selectable>
              {eventMediaCountError(contentCount) ??
                pendingMediaValidationError(pendingMediaAssets)}
            </Text>
          ) : null}
          {isLoadingAssets ? <ActivityIndicator /> : null}
          <View className="flex-row flex-wrap gap-2">
            {existingMedia.map((media) => (
              <View
                accessibilityLabel={mediaLabel(media)}
                className="overflow-hidden rounded-md border border-border bg-muted"
                key={media.id}
                style={{ aspectRatio: 1, width: '31%' }}
              >
                {media.media_type === 'photo' ? (
                  <Image
                    contentFit="cover"
                    source={{ uri: eventMediaPublicUrl(media.storage_path) }}
                    style={{ height: '100%', width: '100%' }}
                  />
                ) : (
                  <View className="flex-1 items-center justify-center gap-1">
                    <Icon as={PlayIcon} className="size-7 text-foreground" />
                    <Text className="text-xs">Video</Text>
                  </View>
                )}
                <Pressable
                  accessibilityLabel={`Remove ${mediaLabel(media)}`}
                  accessibilityRole="button"
                  className="absolute right-0 top-0 size-11 items-center justify-center rounded-bl-md bg-black/70"
                  onPress={() => removeExistingMedia(media)}
                >
                  <Icon as={XIcon} className="size-5 text-white" />
                </Pressable>
              </View>
            ))}
            {pendingMediaAssets.map((asset, index) => (
              <View
                accessibilityLabel={assetLabel(asset, index)}
                className="overflow-hidden rounded-md border border-border bg-muted"
                key={`${asset.uri}-${index}`}
                style={{ aspectRatio: 1, width: '31%' }}
              >
                {asset.type === 'video' ? (
                  <View className="flex-1 items-center justify-center gap-1">
                    <Icon as={PlayIcon} className="size-7 text-foreground" />
                    <Text className="text-xs">Video</Text>
                  </View>
                ) : (
                  <Image
                    contentFit="cover"
                    source={{ uri: asset.uri }}
                    style={{ height: '100%', width: '100%' }}
                  />
                )}
                <Pressable
                  accessibilityLabel={`Remove ${assetLabel(asset, index)}`}
                  accessibilityRole="button"
                  className="absolute right-0 top-0 size-11 items-center justify-center rounded-bl-md bg-black/70"
                  onPress={() => removePendingMedia(index)}
                >
                  <Icon as={XIcon} className="size-5 text-white" />
                </Pressable>
              </View>
            ))}
          </View>
          {!contentCount ? <Text variant="muted">No media selected.</Text> : null}
          <Button
            disabled={contentCount >= MAX_EVENT_MEDIA_ITEMS}
            onPress={() => void pickEventMedia()}
            variant="outline"
          >
            <Text>Add media</Text>
          </Button>
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        error={activeSheet === 'links' && !validationField ? error : null}
        isPresented={activeSheet === 'links'}
        onDismiss={() => dismissSheet('links')}
        title="Additional links"
      >
        <View className="gap-5 py-4">
          <Text variant="muted">
            Paste a full URL or type a domain such as google.com. We’ll add https:// and suggest a
            label when needed.
          </Text>
          {eventLinks.map((link, index) => (
            <View className="gap-2 border-b border-border pb-5" key={link.id ?? `link-${index}`}>
              <View className="flex-row items-center justify-between gap-3">
                <Text variant="label">Link {index + 1}</Text>
                <Pressable
                  accessibilityLabel={`Remove link ${index + 1}`}
                  accessibilityRole="button"
                  className="size-11 items-center justify-center rounded-full active:opacity-60"
                  onPress={() => removeEventLink(index)}
                >
                  <Icon as={XIcon} className="size-5 text-muted-foreground" />
                </Pressable>
              </View>
              <FlatField
                accessibilityLabel={`Link ${index + 1} URL`}
                keyboardType="url"
                onBlur={() => normalizeEventLink(index)}
                onChangeText={(value) => updateEventLink(index, { url: value })}
                placeholder="Paste a link or type google.com"
                value={link.url}
              />
              <FlatField
                accessibilityLabel={`Link ${index + 1} label`}
                onChangeText={(value) => updateEventLink(index, { label: value })}
                placeholder="Label (optional)"
                value={link.label}
              />
            </View>
          ))}
          {eventLinksValidationError(eventLinks) ? (
            <Text className="text-sm text-destructive" selectable>
              {eventLinksValidationError(eventLinks)}
            </Text>
          ) : null}
          {!eventLinks.length ? <Text variant="muted">No additional links.</Text> : null}
          <Button
            disabled={
              eventLinks.length >= MAX_EVENT_LINKS ||
              Boolean(eventLinks.length && !eventLinks[eventLinks.length - 1]?.url.trim())
            }
            onPress={addEventLink}
            variant="outline"
          >
            <Text>{eventLinks.length ? 'Add another link' : 'Add a link'}</Text>
          </Button>
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={activeSheet === 'checkin'}
        onDismiss={() => setActiveSheet(null)}
        title="Check-in"
      >
        <View className="gap-3 py-4">
          {/*
            Member-chain check-in ("checked-in members verify the next runner")
            is not offered. The enum still carries `checked_in_chain` so events
            configured with it before keep working, but nothing can select it.
          */}
          {(
            [
              ['none', 'No check-in', 'Members do not check in for this event.'],
              ['admin_only', 'Admin check-in', 'Only an admin can mark attendees as checked in.'],
            ] as const
          ).map(([value, label, detail]) => (
            <Pressable
              accessibilityRole="radio"
              accessibilityState={{ checked: checkInMode === value }}
              className="min-h-16 justify-center border-b border-border py-3 active:opacity-60"
              key={value}
              onPress={() => {
                markDirty();
                setCheckInMode(value);
              }}
            >
              <Text className={checkInMode === value ? 'font-bold text-primary' : 'font-bold'}>
                {label}
              </Text>
              <Text variant="muted">{detail}</Text>
            </Pressable>
          ))}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={eventDraftPublishEnabled && activeSheet === 'visibility'}
        onDismiss={() => setActiveSheet(null)}
        title="Visibility"
      >
        <View className="gap-3 py-4">
          {(['draft', 'published'] as const).map((value) => (
            <Pressable
              accessibilityRole="radio"
              accessibilityState={{ checked: status === value }}
              className="min-h-16 justify-center border-b border-border py-3 active:opacity-60"
              key={value}
              onPress={() => {
                markDirty();
                setStatus(value);
              }}
            >
              <Text className={status === value ? 'font-bold text-primary' : 'font-bold'}>
                {value === 'published' ? 'Published' : 'Draft'}
              </Text>
              <Text variant="muted">{eventVisibilityDetail(value, isOperator)}</Text>
            </Pressable>
          ))}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={activeSheet === 'attendees'}
        onDismiss={() => setActiveSheet(null)}
        title="Attendees"
      >
        <View className="py-4">
          {event ? (
            <EventAttendees
              enableCheckIn={
                event.checkin_mode !== 'none' && isCheckInWindowOpen(event, getCurrentTime())
              }
              eventId={event.id}
              flat
              onCountChange={setAttendeeCount}
            />
          ) : null}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={
          eventPaymentsEnabled &&
          paymentManagementEnabled &&
          paymentReviewEnabled &&
          activeSheet === 'payments'
        }
        onDismiss={() => setActiveSheet(null)}
        title="Payment review"
      >
        <View className="py-4">
          {event && tenantSlug ? (
            <AdminEventPaymentsPanel eventId={event.id} flat tenantSlug={tenantSlug} />
          ) : null}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={activeSheet === 'notifications'}
        onDismiss={finishPendingNotification}
        title={pendingSavedEvent ? 'Send event update?' : 'Notify attendees'}
      >
        <View className="gap-4 py-4">
          {pendingSavedEvent ? (
            <Text variant="muted">
              {eventUpdatePromptCopy(pendingNotificationReasons) ??
                'Event details changed. Send registered attendees an update now?'}
            </Text>
          ) : null}
          {event && tenantSlug ? (
            <AdminEventNotificationPanel
              eventId={event.id}
              flat
              onSent={pendingSavedEvent ? finishPendingNotification : undefined}
              tenantSlug={tenantSlug}
            />
          ) : null}
          {pendingSavedEvent ? (
            <Button onPress={finishPendingNotification} variant="secondary">
              <Text>Not now</Text>
            </Button>
          ) : null}
        </View>
      </EventEditorSheet>

      <EventEditorSheet
        isPresented={activeSheet === 'cancel'}
        onDismiss={() => setActiveSheet(null)}
        title="Cancel event?"
      >
        <View className="gap-5 py-4">
          <Text variant="muted">
            {isOperator
              ? 'The event remains visible as cancelled and registrations stay available for review.'
              : 'The run will be removed from upcoming lists and remain visible to you as cancelled.'}
          </Text>
          <Button disabled={isCancelling} onPress={() => void cancelEvent()} variant="destructive">
            <Text>{isCancelling ? 'Cancelling…' : 'Cancel event'}</Text>
          </Button>
          <Button disabled={isCancelling} onPress={() => setActiveSheet(null)} variant="secondary">
            <Text>Keep event</Text>
          </Button>
        </View>
      </EventEditorSheet>
    </View>
  );
}
